import { useState, useEffect, useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import { reportsService } from '../../services'
import { parseSalesSummary } from './formatters'
import SummaryCards from './SummaryCards'
import ReportTabs from './ReportTabs'
import InvoiceWiseTable from './ReportTable/InvoiceWiseTable'
import ProductWiseTable from './ReportTable/ProductWiseTable'
import ReportTablePagination from './ReportTable/ReportTablePagination'
import { useInvoiceTable } from './ReportTable/hooks/useInvoiceTable'
import { useProductTable } from './ReportTable/hooks/useProductTable'

function SalesReport({
  enabled,
  salesFilterParams,
  summaryFilterParams,
  sortBy,
  sortOrder,
  onSortChange,
  onOpenInvoice,
  salesSubTab,
  onSalesSubTabChange,
  resetPaginationKey,
  onError,
}) {
  const [summaryState, setSummaryState] = useState({
    totalSales: 0,
    totalInvoices: 0,
    loading: false,
  })

  const handleSort = useCallback(
    (field) => {
      onSortChange(field)
    },
    [onSortChange],
  )

  const invoiceTable = useInvoiceTable({
    enabled,
    salesFilterParams,
    sortBy,
    sortOrder,
    onSort: handleSort,
    resetPaginationKey,
    onError,
  })

  const productTable = useProductTable({
    enabled,
    salesFilterParams,
    sortBy,
    sortOrder,
    onSort: handleSort,
    resetPaginationKey,
    onError,
  })

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    const run = async () => {
      setSummaryState((s) => ({ ...s, loading: true }))
      try {
        const res = await reportsService.getSalesInvoiceWiseReport({
          ...summaryFilterParams,
          page: 1,
          limit: 1,
        })
        if (!cancelled) {
          setSummaryState({ ...parseSalesSummary(res), loading: false })
        }
      } catch (error) {
        console.error('Failed to load sales summary:', error)
        if (!cancelled) {
          setSummaryState({ totalSales: 0, totalInvoices: 0, loading: false })
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [enabled, summaryFilterParams])

  const activeTable = salesSubTab === 0 ? invoiceTable : productTable

  return (
    <>
      <Box sx={{ px: 2, pt: 2 }}>
        <SummaryCards
          totalSales={summaryState.totalSales}
          totalInvoices={summaryState.totalInvoices}
          loading={summaryState.loading}
        />
      </Box>

      <ReportTabs value={salesSubTab} onChange={onSalesSubTabChange} />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
          Total records: {activeTable.pagination?.total || 0}
        </Typography>

        <Box
          sx={{ display: salesSubTab === 0 ? 'block' : 'none' }}
          aria-hidden={salesSubTab !== 0}
        >
          <InvoiceWiseTable
            columns={invoiceTable.columns}
            rows={invoiceTable.rows}
            loading={invoiceTable.loading}
            pagination={invoiceTable.pagination}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onOpenInvoice={onOpenInvoice}
          />
        </Box>

        <Box
          sx={{ display: salesSubTab === 1 ? 'block' : 'none' }}
          aria-hidden={salesSubTab !== 1}
        >
          <ProductWiseTable
            columns={productTable.columns}
            rows={productTable.rows}
            loading={productTable.loading}
            pagination={productTable.pagination}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </Box>

        <ReportTablePagination
          pagination={activeTable.pagination}
          onPageChange={activeTable.setPage}
          onRowsPerPageChange={activeTable.setLimit}
        />
      </Box>
    </>
  )
}

export default SalesReport
